import { type OsanoRegion, classifyOsanoRegion } from './osanoRegion';

export type OsanoBlockingState = {
  // Whether `onInitialized` has fired at least once.
  initialized: boolean;
  region: OsanoRegion | null;
  // Whether the visitor has made *any* saved choice, including a rejection.
  // Monotonic: once true, stays true for the life of the page.
  saved: boolean;
  // Whether the page must currently block interaction for a usable,
  // unanswered EU choice. Never true outside the `eu` region, and never true
  // once `saved` is true.
  required: boolean;
};

export type OsanoBlockingListener = (state: OsanoBlockingState) => void;

const initialState: OsanoBlockingState = {
  initialized: false,
  region: null,
  saved: false,
  required: false,
};

/**
 * Pure, DOM-free state machine for the EU "choice required" blocking
 * decision. Deliberately separate from the React/DOM layer (focus trap,
 * inert bookkeeping, CSS class toggling) so the event-ordering edge cases
 * from the handover doc — synchronous re-init, `onConsentSaved` arriving
 * before `onInitialized`, etc. — can be tested without mounting anything.
 *
 * `saved` is a one-way latch: once a choice is observed (via either an
 * initialized consent value or a saved event), it never flips back to
 * `false`. This matches the requirement that a returning visitor, even one
 * who previously rejected everything, is never blocked again by a later
 * jurisdiction read.
 */
export const createOsanoBlockingController = () => {
  let state: OsanoBlockingState = initialState;
  const listeners = new Set<OsanoBlockingListener>();

  // Avoid `for...of` over the Set: the package's tsconfig targets ES5
  // without `downlevelIteration`, which silently produces a no-op loop
  // here instead of a compile error.
  const notify = () => {
    listeners.forEach((listener) => listener(state));
  };

  const computeRequired = (region: OsanoRegion | null, saved: boolean) =>
    region === 'eu' && !saved;

  return {
    getState: (): OsanoBlockingState => state,

    subscribe: (listener: OsanoBlockingListener): (() => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Call from `window.Osano('onInitialized', ...)`. `consent` is
     * `undefined` when the visitor hasn't chosen yet — any other value
     * (including an explicitly-rejected one) counts as saved. Safe to call
     * more than once (the SDK may fire synchronously on an already-initialized
     * late subscription, or re-fire after a jurisdiction re-resolution).
     */
    handleInitialized: (
      jurisdiction: unknown,
      consent: unknown
    ): OsanoBlockingState => {
      const region = classifyOsanoRegion(jurisdiction);
      const saved = state.saved || consent !== undefined;
      state = {
        initialized: true,
        region,
        saved,
        required: computeRequired(region, saved),
      };
      notify();
      return state;
    },

    /**
     * Call from `window.Osano('onConsentSaved', ...)`. May fire before
     * `handleInitialized` in rare races — `saved` latches true regardless of
     * call order, and `required` is recomputed from whatever region is known
     * at the time (defaulting to not-required until a region is known, since
     * blocking a never-classified visitor would violate "fail open").
     */
    handleConsentSaved: (): OsanoBlockingState => {
      state = {
        ...state,
        saved: true,
        required: computeRequired(state.region, true),
      };
      notify();
      return state;
    },

    /**
     * Resets to the initial, never-initialized state. For tests; the real
     * controller is created fresh per page load.
     */
    reset: (): void => {
      state = initialState;
    },
  };
};

export type OsanoBlockingController = ReturnType<
  typeof createOsanoBlockingController
>;
